import React from "react";
import "./banner.scss";
import { useTranslation } from "react-i18next";
import { MotifChip } from "@ey-xd/motif-react";
import { Link } from "react-router-dom";
import { useCase } from "@/features/case/use-case";

export interface BannerElement {
  label: string;
  value: string;
  elementKey?: string;
  addedClassName?: string;
  id?: string;
  type?: string;
  display?: boolean;
  elementNode?: React.ReactNode;
  className?: string;
  navLink?: string;
}

interface BannerProps {
  bannerName: string;
  bannerNameValue: string;
  elements: BannerElement[];
  addedClassName?: string;
  headerClassName?: string;
  childClassName?: string;
}

export default function Banner({
  bannerName,
  bannerNameValue,
  elements,
  addedClassName,
  headerClassName,
  childClassName,
}: BannerProps) {
  const { t } = useTranslation();
  let { caseId } = useCase();

  return (
    <>
      <div className="display-flex align-items-center banner pad-v-20 pad-h-40">
        <div
          className={
            !headerClassName ? "pad-r-60 banner-title" : headerClassName
          }
        >
          <h2 className="motif-subtitle-default-regular mar-t-0 mar-b-10">
            {t(bannerName)}
          </h2>
          <h3 className="motif-h3-default-regular banner-title-value mar-v-0 break-word">
            {bannerNameValue}
          </h3>
        </div>
        <div className="display-flex">
          {elements.map((element, index) =>
            element?.type === "image" ? (
              element?.display ? (
                <>
                  <div
                    key={index}
                    className={
                      childClassName
                        ? `${childClassName} ${element.className}`
                        : `pad-l-60 ${element?.className}`
                    }
                  >
                    <div className="motif-body1-default-regular">
                      {t(element.label)}
                    </div>
                    <div
                      style={{
                        marginTop: "10px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {element?.elementNode}
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )
            ) : element?.type === "HTMLElement" ? (
              element?.display ? (
                <div
                  className={
                    childClassName
                      ? `${childClassName} ${element.className}`
                      : `pad-l-60 ${element?.className}`
                  }
                >
                  <div className="motif-body1-default-regular">
                    {t(element.label)}
                  </div>
                  {element?.elementNode}
                </div>
              ) : (
                <></>
              )
            ) : (
              <div
                key={index}
                className={childClassName ? childClassName : `pad-l-60`}
              >
                <div className="motif-body1-default-regular">
                  {t(element.label)}
                </div>
                {element?.value?.toLowerCase() === "open" ? (
                  <MotifChip
                    dotSide="left"
                    title="Case Status"
                    className="pad-t-5"
                    type="chip"
                    variant="success"
                  >
                    {element?.value?.toLowerCase() === "open"
                      ? t("global.globalBanner.statusOpen")
                      : t("global.globalBanner.statusSuccess")}
                  </MotifChip>
                ) : element?.value?.toLowerCase() === "close" ? (
                  <MotifChip
                    dotSide="left"
                    title="Case Status"
                    className="pad-t-5"
                    type="chip"
                    variant={
                      element?.value?.toLowerCase() === "close"
                        ? "warning"
                        : "error"
                    }
                  >
                    {element?.value?.toLowerCase() === "close"
                      ? t("global.globalBanner.statusClose")
                      : t("global.globalBanner.statusFailed")}
                  </MotifChip>
                ) : (
                  <div
                    className={`motif-h6-default-regular banner-${element.elementKey} ${addedClassName} ${element?.className}`}
                  >
                    {element?.id ? (
                      <Link
                        style={{ textDecoration: "none" }}
                        className={`motif-h6-default-regular banner-${element.elementKey} ${addedClassName}`}
                        to={
                          element?.navLink
                            ? element.navLink
                            : `/case/${caseId}/creditor/${element.id}`
                        }
                        state={{ from: element.id }}
                      >
                        <span className="text-shadow-underline">
                          {element.value}
                        </span>
                      </Link>
                    ) : (
                      element.value
                    )}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
      <div className="mobile-view-banner pad-v-20 pad-h-40">
        <div
          style={{ columnGap: "5px" }}
          className="motif-row align-items-center"
        >
          <span
            className="motif-subtitle-default-regular "
            style={{ overflowWrap: "anywhere", flex: 1, fontSize: "16px" }}
          >
            {t(bannerName)}
          </span>
          <span
            className="motif-h6-default-regular  banner-title-value"
            style={{ overflowWrap: "anywhere", flex: 1, fontSize: "16px" }}
          >
            {bannerNameValue}
          </span>
        </div>
        <hr />
        {elements.map((element, index) => (
          <>
            {element?.type === "image" ? (
              element?.display && element?.elementNode ? (
                <div
                  key={index}
                  className={
                    childClassName
                      ? `${childClassName} ${element.className}`
                      : `pad-l-60 ${element?.className}`
                  }
                >
                  {element?.elementNode}
                </div>
              ) : (
                <></>
              )
            ) : element?.type === "HTMLElement" ? (
              element?.display ? (
                <div
                  style={{ columnGap: "5px" }}
                  className="motif-row align-items-center"
                >
                  <span
                    className="motif-subtitle-default-regular  "
                    style={{
                      overflowWrap: "anywhere",
                      flex: 1,
                      fontSize: "14px",
                    }}
                  >
                    {t(element.label)}
                  </span>
                  <span style={{ flex: 1 }}>{element?.elementNode}</span>
                </div>
              ) : (
                <></>
              )
            ) : (
              <div
                style={{ columnGap: "5px" }}
                className="motif-row align-items-center"
              >
                <span
                  className="motif-subtitle-default-regular  "
                  style={{
                    overflowWrap: "anywhere",
                    flex: 1,
                    fontSize: "14px",
                  }}
                >
                  {t(element.label)}
                </span>
                {element?.value?.toLowerCase() === "open" ? (
                  <span style={{ flex: 1 }}>
                    <MotifChip
                      dotSide="left"
                      title="Case Status"
                      className="pad-t-5 "
                      type="chip"
                      variant="success"
                    >
                      {element?.value?.toLowerCase() === "open"
                        ? t("global.globalBanner.statusOpen")
                        : t("global.globalBanner.statusSuccess")}
                    </MotifChip>
                  </span>
                ) : element?.value?.toLowerCase() === "close" ? (
                  <span style={{ flex: 1 }}>
                    <MotifChip
                      dotSide="left"
                      title="Case Status"
                      className="pad-t-5"
                      type="chip"
                      variant={
                        element?.value?.toLowerCase() === "close"
                          ? "warning"
                          : "error"
                      }
                    >
                      {element?.value?.toLowerCase() === "close"
                        ? t("global.globalBanner.statusClose")
                        : t("global.globalBanner.statusFailed")}
                    </MotifChip>
                  </span>
                ) : (
                  <span
                    className="motif-h6-default-regular"
                    style={{
                      overflowWrap: "anywhere",
                      flex: 1,
                      fontSize: "14px",
                    }}
                  >
                    {element?.id ? (
                      <Link
                        style={{ textDecoration: "none" }}
                        className={`motif-h6-default-regular banner-${element.elementKey} ${addedClassName}`}
                        to={
                          element?.navLink
                            ? element.navLink
                            : `/case/${caseId}/creditor/${element.id}`
                        }
                        state={{ from: element.id }}
                      >
                        <span className="text-shadow-underline">
                          {element.value}
                        </span>
                      </Link>
                    ) : (
                      element.value
                    )}{" "}
                  </span>
                )}
              </div>
            )}
            <hr />
          </>
        ))}
      </div>
    </>
  );
}
